import * as React from 'react';
const { Portal }: any = require('react-portal');
import Transition, { TransitionProps } from 'react-transition-group/Transition';
import { Backdrop, BackdropColor } from '../Backdrop';
import { styles } from './styles';

import uuid from 'uuid/v4';
import { ModalContent } from './components/ModalContent';
import { ModalHeader } from './components/ModalHeader';
import { buildReactStateHandler, makeDebugger } from '@lib';
import { modalRegistry } from './modalRegistry';
import { ModalInner } from './ModalInner';
import { ModalWrapper } from './ModalWrapper';
import { ModalProps } from './types';
import { cx } from 'emotion';
export { ModalProps } from './types';

const CloseSVG = ({ fill = '#000', ...props }) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <path
      fill={fill}
      fillRule="nonzero"
      d="M23.815 22.974c.555.555-.274 1.406-.839.839L12 12.837 1.023 23.813c-.562.562-1.394-.284-.838-.839L11.16 12 .185 1.023c-.56-.56.28-1.398.838-.838L12 11.16 22.976.185c.56-.56 1.4.28.84.838L12.838 12l10.976 10.975z"
    />
  </svg>
);

const debug = makeDebugger('Modal');
const stateHandler = buildReactStateHandler<ModalState>();

type ModalState = Readonly<{
  hide: boolean;
  exiting: boolean;

  scrolling: boolean;
}>;

export class Modal extends React.Component<ModalProps, ModalState> {
  static Content = ModalContent;
  static Header = ModalHeader;

  static defaultProps = {
    active: true,
    backdrop: true,
    backdropColor: 'dark' as BackdropColor,
    disableBackdropClick: false,
    disableEscapeKeydown: false,
    size: 'normal',
    node: document.body,
    closeIcon: true
  };

  private id: string = uuid();
  private animationRequestId: number | null = null;
  private modalRef: React.RefObject<HTMLDivElement> = React.createRef();

  readonly state: ModalState = {
    hide: !this.props.open,
    exiting: !this.props.open,
    scrolling: false
  };

  // ----------------------------------------
  // Lifecycle
  // ----------------------------------------

  componentDidUpdate(prevProps: ModalProps, prevState: ModalState) {
    if (this.props.open && !prevProps.open) {
      this.resetAnimationState();
    } else if (!this.props.open && this.state.exiting && !this.state.hide) {
      this.finishCloseAnimation();
    }
  }

  componentWillUnmount() {
    this.handleOnUnmounted();
  }

  // ----------------------------------------
  // Callback handling
  // ----------------------------------------

  handleOnCloseRequest: ModalProps['onClose'] = (e, context) => {
    debug('handleOnCloseRequest()');
    this.props.onClose(e, context);
  };

  handleOnMount: TransitionProps['onEnter'] = () => {
    modalRegistry.add(this.id, { dimmed: !!this.props.backdrop });
  };

  handleOnMounted: TransitionProps['onEntered'] = () => {
    debug('handleOnMounted()');
    this.setScrollState();
  };

  handleOnUnmount: TransitionProps['onExit'] = () => {
    modalRegistry.remove(this.id);
  };

  handleOnUnmounted = () => {
    debug('handleOnUnmounted()');
    if (this.animationRequestId) {
      cancelAnimationFrame(this.animationRequestId);
    }
    this.setState(
      stateHandler(draft => {
        draft.exiting = true;
        draft.hide = false;
        draft.scrolling = false;
      })
    );
  };

  // ----------------------------------------
  // Internal helpers
  // ----------------------------------------

  private setScrollState = () => {
    if (this.modalRef.current) {
      const { height } = this.modalRef.current.getBoundingClientRect();
      const scrolling = height > window.innerHeight;

      if (this.state.scrolling !== scrolling) {
        this.setState(
          stateHandler(draft => {
            draft.scrolling = scrolling;
          })
        );
      }
    }

    this.animationRequestId = requestAnimationFrame(this.setScrollState);
  };

  // Handling animation flow

  /**
   * Reset close animation state when a modal goes from `closed` to `open`
   */
  private resetAnimationState = () => {
    this.setState(
      stateHandler(draft => {
        draft.exiting = false;
        draft.hide = false;
      })
    );
  };

  /**
   * Animation has completed - now remove the node from the DOM
   */
  private finishCloseAnimation = () => {
    window.setTimeout(() => {
      this.setState(
        stateHandler(draft => {
          draft.hide = true;
        })
      );
    }, 500);
  };

  // ----------------------------------------
  // Render
  // ----------------------------------------

  renderCloseIcon = () => {
    return this.props.closeIcon ? (
      <CloseSVG
        fill={this.props.backdropColor === 'dark' ? '#fff' : '#333'}
        role="button"
        tabIndex={0}
        aria-label="Close"
        title="close"
        onClick={this.handleOnCloseRequest}
        className={cx(styles.exitButton, 'icon')}
      />
    ) : // <CloseSVG fill={'black'} />
    null;
  };

  renderBackdrop = () => {
    if (this.props.backdrop) {
      return <Backdrop open={this.props.open} color={this.props.backdropColor} />;
    }

    return null;
  };

  render() {
    const { exiting, hide, scrolling } = this.state;
    const { open, children, backdrop, size = 'normal', disableBackdropClick, disableEscapeKeydown } = this.props;

    const closed = !open && exiting && hide;

    if (closed) {
      return null;
    }

    return (
      <Portal node={this.props.node}>
        <ModalWrapper scrolling={scrolling}>
          <Transition
            appear={true}
            unmountOnExit={true}
            in={open}
            timeout={{
              enter: 0,
              exit: 250
            }}
            onEnter={this.handleOnMount}
            onEntered={this.handleOnMounted}
            onExit={this.handleOnUnmount}
            onExited={this.handleOnUnmounted}
          >
            {state => {
              return (
                <ModalInner
                  open={open}
                  forwardedRef={this.modalRef}
                  transitionStatus={state}
                  size={size}
                  scrolling={scrolling}
                  backdrop={backdrop}
                  onCloseRequest={this.handleOnCloseRequest}
                  disableBackdropClick={disableBackdropClick}
                  disableEscapeKeydown={disableEscapeKeydown}
                >
                  {children}
                  {this.renderCloseIcon()}
                </ModalInner>
              );
            }}
          </Transition>
          {this.renderBackdrop()}
        </ModalWrapper>
      </Portal>
    );
  }
}
